import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import Chart from "react-apexcharts";
import ReactJson from "react-json-view";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  Stack,
  Tabs,
  Tab,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
} from "@mui/material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";

import { getResStatusString, isReqBlockedBySD } from "../../application/analytics/common";

import colors from "../../../../utils/colors";
import { WafType, SenseDefenceOperator } from "../../../../utils/constants";
import { LAYERD_SIGNATURE_WAF_LABEL, getWafSourceDisplayLabel } from "./wafSourceLabels";
import { getSeverityColor } from "../../../../utils/functions";
import { getSeverityName } from "../../../../utils/functions";
import { getEventTitle, getCategoryTitle } from "../services/web_application/DetectionDetail";
import useAuth from "../../../../hooks/useAuth";
import useEvent from "../../../../hooks/user/useEvent";
import { outlinedButtonSx } from "../../../common/Button";

const circleOptions = {
  chart: {
    type: "radialBar",
    offsetY: -80,
    sparkline: {
      enabled: false,
    },
  },
  plotOptions: {
    radialBar: {
      hollow: {
        margin: 50,
        size: "0%",
        background: "#fff",
        position: "front",
      },
      track: {
        background: "#D4D4D4",
        strokeWidth: "50%",
        margin: 50, // margin is in pixels
        dropShadow: {
          enabled: false,
        },
      },
      dataLabels: {
        name: {
          show: false,
        },
        value: {
          offsetY: 5,
          fontSize: "28px",
          fontWeight: "bold",
        },
      },
    },
  },
  fill: {
    type: "gradient",
    gradient: {
      shadeIntensity: 0,
    },
  },
  colors: ["#FFDB43"],
  stroke: {
    lineCap: "round",
  },
};

const requestTitleSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const requestValueSx =  { fontSize: 14, lineHeight: "25px", color: colors.neutral[800] };

const getType = (type) => {
  const label = getWafSourceDisplayLabel(type);
  if (label) return label;
  if (type === WafType.FIREWALL) return "Firewall";
  return LAYERD_SIGNATURE_WAF_LABEL;
};

const getOperatorDescription = (operator) => {
  switch (operator) {
    case SenseDefenceOperator.rx:
      return "Regular Expression";
    case SenseDefenceOperator.pm:
      return "Case Insensitive Match";
    case SenseDefenceOperator.ge:
      return "Greater Than";
    case SenseDefenceOperator.eq:
      return "Equals to";
    case SenseDefenceOperator.le:
      return "Less Than";
    case SenseDefenceOperator.detectSQLi:
      return "SQL Injection";
    case SenseDefenceOperator.detectXSS:
      return "Cross Site Scripting";
    default:
      return null;
  }
};

const getLabel = (eve, cate) => {
  let maxSeverity = 0;
  switch (cate) {
    case WafType.SIGNATURE:
      eve?.forEach((e) => {
        if (e.waf_type !== WafType.SIGNATURE) return;
        if (e.severity > maxSeverity) maxSeverity = e.severity;
      });
      break;
    case WafType.SENSEDEFENCE_SIGNATURE:
      eve?.forEach((e) => {
        if (e.waf_type !== WafType.SENSEDEFENCE_SIGNATURE) return;
        if (e.severity > maxSeverity) maxSeverity = e.severity;
      });
      break;
    default:
      break;
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={"center"}
      sx={{ borderRadius: "100px", background: getSeverityColor(maxSeverity), width: 70, height: 25 }}
    >
      <Typography sx={ { fontSize: 14, lineHeight: "25px", color: "white" }}>{getSeverityName(maxSeverity)}</Typography>
    </Box>
  );
};

export const getStatusLabel = (label) => {
  let backgroundColor =
    label === "Passed"
      ? colors.green[10]
      : label === "Blocked"
      ? colors.red[10]
      : label === "Challenged"
      ? colors.yellow[10]
      : colors.neutral[10];
  let borderColor =
    label === "Passed"
      ? colors.green[100]
      : label === "Blocked"
      ? colors.red[100]
      : label === "Challenged"
      ? colors.yellow[100]
      : colors.neutral[800];
  let fontColor =
    label === "Passed"
      ? colors.green[300]
      : label === "Blocked"
      ? colors.red[100]
      : label === "Challenged"
      ? colors.yellow[100]
      : colors.neutral[800];
  return (
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      sx={{ minWidth: 80, width: 80, height: 25, bgcolor: backgroundColor, borderRadius: "20px", border: `1px solid ${borderColor}` }}
    >
      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: fontColor }}>{label}</Typography>
    </Stack>
  );
};

const WAFEventView = () => {
  const { categoryId, eventId } = useParams();
  const [searchParams] = useSearchParams();

  const { isAuthenticated } = useAuth();
  const { getEvent, event, setErr } = useEvent();

  const [selectedTab, setSelectedTab] = useState("timeline");

  const [sigCount, setSigCount] = useState(0);
  const [sdSigCount, setSdSigCount] = useState(0);
  const [fwCount, setFwCount] = useState(0);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getEvent(eventId);
    }
    return () => setErr(null);
  }, [isAuthenticated, setErr, getEvent, eventId]);

  useEffect(() => {
    let _fwCount = 0;
    let _sigCount = 0;
    let _sdSigCount = 0;
    if (event !== null && event?.type && event?.type.length > 0) {
      event?.type.forEach((e) => {
        if (e?.waf_type === WafType.SIGNATURE) _sigCount += 1;
        if (e?.waf_type === WafType.SENSEDEFENCE_SIGNATURE) _sdSigCount += 1;
        if (e?.waf_type === WafType.FIREWALL) _fwCount += 1;
      });
    }
    setSigCount(_sigCount);
    setSdSigCount(_sdSigCount);
    setFwCount(_fwCount);
  }, [event]);

  return (
    <>
      <Helmet title="WAF Event" />
      {event === null && (
        <Box sx={{ width: "100%", position: "fixed", top: 66 }}>
          <LinearProgress
            sx={{
              "& .MuiLinearProgress-bar1Indeterminate": { backgroundColor: colors.primary[200] },
              "& .MuiLinearProgress-bar2Indeterminate": { backgroundColor: colors.primary[200] },
              height: 4,
            }}
          />
        </Box>
      )}
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <Stack direction={"row"} alignItems={"center"} spacing={1} onClick={() => window.history.back()} sx={{ cursor: "pointer" }}>
            <LayerdIcon icon={DETECTION_ICONS.back} size={20} color="primaryLight" />
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
              WAF Event
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
              /
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
              {getCategoryTitle(categoryId)}
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
              /
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
              {getEventTitle(categoryId, decodeURIComponent(searchParams.get("data")))}
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
              /
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
              Activity log
            </Typography>
          </Stack>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3}>
            <Tabs
              value={selectedTab}
              onChange={(_, v) => setSelectedTab(v)}
              sx={{
                minHeight: "inherit",
                height: 32,
                "& .MuiTabs-flexContainer": { border: "none", px: 0, py: 0, gap: "8px", height: 32 },
                "& .MuiTabs-indicator": { height: "1px", backgroundColor: colors.secondary[300] },
                "& button": { p: 0, minWidth: "inherit", minHeight: "inherit" },
                "& button .MuiTypography-root": { color: colors.neutral[700] },
                "& .Mui-selected .MuiTypography-root": { color: colors.neutral[1000] },
              }}
            >
              <Tab
                value={"timeline"}
                label={<Typography sx={ { fontSize: 14 }}>Timeline</Typography>}
                sx={{ width: 106 }}
              />
              <Tab
                value={"rawdata"}
                label={<Typography sx={ { fontSize: 14 }}>Raw data</Typography>}
                sx={{ width: 106 }}
              />
            </Tabs>
            <Button
              startIcon={<LayerdIcon icon={DETECTION_ICONS.download} size={16} color="neutralDark" />}
              sx={{ width: 128, height: 32, ...outlinedButtonSx }}
            >
              Download
            </Button>
          </Stack>
          {selectedTab === "timeline" && (
            <Stack spacing={6}>
              <Stack direction={"row"} alignItems={"center"} spacing={6} sx={{ py: 3 }}>
                <Stack spacing={1}>
                  <Typography
                    sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                  >
                    Host name
                  </Typography>
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                    {event?.host_name}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography
                    sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                  >
                    Source IP
                  </Typography>
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                    <ReactCountryFlag
                      countryCode={event?.country_iso_code}
                      svg
                      style={{
                        width: "26px",
                        height: "18px",
                      }}
                    />{" "}
                    {event?.src_ip}
                  </Typography>
                </Stack>
              </Stack>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.request} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Reqeust
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Stack direction={"row"} alignItems={"center"} spacing={6}>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Path</Typography>
                        <Typography sx={requestValueSx}>{event?.uri}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Method</Typography>
                        <Typography sx={requestValueSx}>{event?.method}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Host</Typography>
                        <Typography sx={requestValueSx}>{event?.host_name}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Status</Typography>
                        {getStatusLabel(getResStatusString(event?.resStatus))}
                      </Stack>
                    </Stack>
                    <Box>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <Stack direction={"row"} alignItems={"center"} spacing={2}>
                            <LayerdIcon icon={DETECTION_ICONS.header} size={20} color="primary" />
                            <Typography
                              sx={{
                                fontSize: 16,
                                lineHeight: "24px",
                                fontWeight: "medium",
                                color: colors.neutral[1000],
                              }}
                            >
                              Headers
                            </Typography>
                          </Stack>
                        </Grid>
                        {event?.request_headers &&
                          Object.keys(event?.request_headers)
                            ?.filter(
                              (key) => !["cache-control", "pragma", "accept-encoding", "connection", "accept", "referer"].includes(key)
                            )
                            ?.map((key, index) => (
                              <Grid key={`key_${index}`} item xs={12} md={6}>
                                <Stack spacing={1}>
                                  <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                                    {key}
                                  </Typography>
                                  <Typography sx={requestValueSx}>{event?.request_headers[key]}</Typography>
                                </Stack>
                              </Grid>
                            ))}
                      </Grid>
                    </Box>
                    <Box>
                      <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={4}>
                            <Grid item xs={12}>
                              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                                <LayerdIcon icon={DETECTION_ICONS.cache} size={20} color="primary" />
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    lineHeight: "24px",
                                    fontWeight: "medium",
                                    color: colors.neutral[1000],
                                  }}
                                >
                                  Caching & Compression
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack spacing={1}>
                                <Typography sx={requestTitleSx}>Cache-Control</Typography>
                                <Typography sx={requestValueSx}>{event?.request_headers?.["cache-control"] ?? "—"}</Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack spacing={1}>
                                <Typography sx={requestTitleSx}>Pragma</Typography>
                                <Typography sx={requestValueSx}>{event?.request_headers?.["pragma"] ?? "—"}</Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack spacing={1}>
                                <Typography sx={requestTitleSx}>Accept-Encoding</Typography>
                                <Typography sx={requestValueSx}>{event?.request_headers?.["accept-encoding"] ?? "—"}</Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack spacing={1}>
                                <Typography sx={requestTitleSx}>Connection</Typography>
                                <Typography sx={requestValueSx}>{event?.request_headers?.["connection"] ?? "—"}</Typography>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={4}>
                            <Grid item xs={12}>
                              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                                <LayerdIcon icon={DETECTION_ICONS.contentNegotiation} size={20} color="primary" />
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    lineHeight: "24px",
                                    fontWeight: "medium",
                                    color: colors.neutral[1000],
                                  }}
                                >
                                  Content Negotiation
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack spacing={1}>
                                <Typography sx={requestTitleSx}>Accepts</Typography>
                                <Typography sx={requestValueSx}>{event?.request_headers?.["accept"] ?? "—"}</Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack spacing={1}>
                                <Typography sx={requestTitleSx}>Referer</Typography>
                                <Typography sx={requestValueSx}>{event?.request_headers?.["referer"] ?? "—"}</Typography>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.wafDetection} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      WAF detection
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Stack direction={"row"} alignItems={"center"} spacing={4}>
                      <Stack spacing={2}>
                        <Stack direction={"row"} alignItems={"center"} spacing={1}>
                          <LayerdIcon icon={DETECTION_ICONS.requestBlocked} size={20} color="primary" />
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              fontWeight: "medium",
                              color: colors.neutral[1000],
                            }}
                          >
                            Request blocked
                          </Typography>
                        </Stack>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], pl: 7 }}>
                          {isReqBlockedBySD(event?.resStatus) ? "True" : "False"}
                        </Typography>
                      </Stack>
                      {fwCount > 0 && (
                        <Stack spacing={2}>
                          <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <LayerdIcon icon={DETECTION_ICONS.ml} size={20} color="primary" />
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                fontWeight: "medium",
                                color: colors.neutral[1000],
                              }}
                            >
                              Firewall
                            </Typography>
                          </Stack>
                          <Typography
                            sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], pl: 7 }}
                          ></Typography>
                        </Stack>
                      )}
                      {sigCount > 0 && (
                        <Stack spacing={2}>
                          <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <LayerdIcon icon={DETECTION_ICONS.ml} size={20} color="primary" />
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                fontWeight: "medium",
                                color: colors.neutral[1000],
                              }}
                            >
                              {LAYERD_SIGNATURE_WAF_LABEL}
                            </Typography>
                          </Stack>
                          <Box sx={{ pl: 7 }}>{getLabel(event?.type, WafType.SIGNATURE)}</Box>
                        </Stack>
                      )}
                    </Stack>
                    <Box sx={{ width: "100%" }}>
                      <Table
                        sx={{
                          "& .MuiTableCell-head": { py: 3, px: 3 },
                          "& .MuiTableCell-body": { py: 2, px: 3 },
                          "& .MuiTableCell-root": { borderBottom: "none" },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <Typography sx={requestTitleSx}>WAF type</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={requestTitleSx}>Rule ID</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={requestTitleSx}>Attack type</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={requestTitleSx}>Paranoia level</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={requestTitleSx}>Severity</Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {event?.type?.map((item, index) => (
                            <TableRow key={`type_${index}`}>
                              <TableCell>
                                <Typography sx={requestValueSx}>{getType(item?.waf_type)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={requestValueSx}>{item?.sec_rule_id ?? "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={requestValueSx}>{item?.attack_type ?? "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={requestValueSx}>{item?.paranoia_level ?? "—"}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={requestValueSx}>{item?.severity ? getSeverityName(item?.severity) : "—"}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.match} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Matched criteria
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ width: "100%" }}>
                    <Table
                      sx={{
                        "& .MuiTableCell-head": { py: 3, px: 3 },
                        "& .MuiTableCell-body": { py: 2, px: 3 },
                        "& .MuiTableCell-root": { borderBottom: "none" },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography sx={requestTitleSx}>SecRuleId</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={requestTitleSx}>Field</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={requestTitleSx}>Value</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={requestTitleSx}>Operator</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={requestTitleSx}>Parameter</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(event?.raw?.messages ?? [])?.map((message, index) => (
                          <TableRow key={`match_${index}`}>
                            <TableCell>
                              <Typography sx={requestValueSx}>{message.id ?? "—"}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={requestValueSx}>{message?.key ?? "—"}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={requestValueSx}>{message?.value ?? "—"}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={requestValueSx}>
                                {message.operator ? getOperatorDescription(message.operator) || message.operator : "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={requestValueSx}>{message?.parameter ?? "—"}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.response} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Response
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Box>
                      <Stack direction={"row"} spacing={6}>
                        <Stack
                          spacing={2}
                          sx={{
                            border: `solid 1px ${isReqBlockedBySD(event?.resStatus) ? colors.red[100] : colors.primary[300]}`,
                            backgroundColor: isReqBlockedBySD(event?.resStatus) ? colors.red["10"] : colors.primary["alpha"],
                            borderRadius: "8px",
                            px: 4,
                            py: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              fontWeight: "medium",
                              color: colors.neutral[1000],
                            }}
                          >
                            Blocked By Layerd AI
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              color: isReqBlockedBySD(event?.resStatus) ? colors.red[100] : colors.primary[300],
                            }}
                          >
                            {isReqBlockedBySD(event?.resStatus) ? "True" : "False"}
                          </Typography>
                        </Stack>
                        <Stack spacing={2} sx={{ px: 4, py: 2 }}>
                          <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <LayerdIcon icon={DETECTION_ICONS.responseStatus} size={20} color="primary" />
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                fontWeight: "medium",
                                color: colors.neutral[1000],
                              }}
                            >
                              Response
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              color: colors.primary[300],
                              pl: 7,
                            }}
                          >
                            {event?.resStatus}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                    {event?.response_headers && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "bold" }}>
                            Headers
                          </Typography>
                        </Grid>
                        {Object.keys(event?.response_headers)?.map((key, index) => (
                          <Grid key={`key_${index}`} item xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>
                                {key}
                              </Typography>
                              <Typography sx={requestValueSx}>{event?.response_headers[key]}</Typography>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.ipReputation} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      IP Reputation
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction={"row"} alignItems={"center"} spacing={6}>
                    <Box sx={{ height: 150, width: 200 }}>
                      <Chart options={circleOptions} series={[75]} type="radialBar" height={320} width={320} />
                    </Box>
                    <Stack spacing={1} sx={{ pl: 10 }}>
                      <Typography
                        sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                      >
                        IP address
                      </Typography>
                      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                        Abuse confident score
                      </Typography>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
          {selectedTab === "rawdata" && (
            <Box>
              <ReactJson
                name="raw"
                src={event?.raw}
                displayDataTypes={false}
                style={{ wordBreak: "break-word", fontSize: 13 }}
              />
            </Box>
          )}
        </Stack>
      </Box>
    </>
  );
};
export default WAFEventView;
